import type { PaymentLogDetailResponse } from '../types/api'

export function display(value: unknown): string {
  if (value == null || value === '') return '-'
  return String(value)
}

export function fullName(...parts: unknown[]): string {
  const name = parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
  return name || '-'
}

export function toYmd(value: unknown): string {
  if (value == null || value === '') return ''
  const raw = String(value)
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function formatDate(value: unknown): string {
  const ymd = toYmd(value)
  if (!ymd) return '-'
  const [year, month, day] = ymd.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

export function formatDateTime(value: unknown): string {
  if (!value) return '-'
  const raw = String(value)
  const numeric = Number(raw)
  const d =
    Number.isFinite(numeric) && /^\d{10}$/.test(raw)
      ? new Date(numeric * 1000)
      : new Date(raw)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export function normalizeBoolean(value: unknown): boolean {
  return value === true || value === '1' || value === 1
}

export function normalizeMulti(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  if (value == null || value === '') return []
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function amountForApi(value: string): string {
  return String(value ?? '').replace(/[$,\s]/g, '')
}

export function unixSecondsToYmd(value: unknown): string {
  const seconds = Number(value)
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  return toYmd(new Date(seconds * 1000).toISOString())
}

export function isDecisionAllowed(detail: PaymentLogDetailResponse | null): boolean {
  return ['CHALLENGED', 'CHARGEBACK_COMPLETE'].includes(
    String(detail?.chargeback_status ?? ''),
  )
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
